/**
 * Leg survey — the other legs of the package a print belongs to.
 *
 * The panel this replaces ranked prints that *looked* similar and asked the reader to link
 * them by hand, because the exchange print time was never recorded on the tape. It is now,
 * so membership is read rather than guessed: legs of one package cross together on one
 * exchange at one instant, and the exchange flags the print with a multi-leg condition.
 *
 * Two clicks at most. Pick an instant — packages listed first, because a contract's biggest
 * prints are usually NOT its package prints — and every leg that crossed with it appears.
 * Expiry is not constrained by default, so calendars and diagonals show up as themselves.
 *
 * What it will not do is call something a strategy on our own say-so. When the exchange did
 * not flag the instant, the legs are shown as prints that happened to coincide, and labelled
 * that way.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import type { NormalizedFlowLeg } from '../../types/flowPatternLeg';
import { classifyLinkedLegs } from '../../utils/flowPatternRecognition';
import {
  fetchLegAnchors,
  fetchLegSurvey,
  msOfDayLabel,
  type LegAnchor,
  type LegSurvey,
} from '../../services/flowLegSurvey';

export interface FlowPatternRecognitionPanelProps {
  open: boolean;
  onClose: () => void;
  anchor: NormalizedFlowLeg | null;
  dataset: NormalizedFlowLeg[];
  language: string;
  isScreenerContext?: boolean;
  /** Session to survey, `YYYY-MM-DD`. Falls back to the anchor print's own day. */
  tapeSessionDayYmd?: string;
  overlayZClass?: string;
  compact?: boolean;
}

const fmtUsd = (n: number): string =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(1)}K`
      : `$${n.toFixed(0)}`;

/** The session a print belongs to, from the explicit prop or the print's own clock. */
function sessionOf(anchor: NormalizedFlowLeg | null, explicit?: string): string {
  if (explicit) return explicit;
  const ms = anchor?.timestampMs;
  if (typeof ms === 'number' && Number.isFinite(ms) && ms > 0) {
    return new Date(ms).toISOString().slice(0, 10);
  }
  return new Date().toISOString().slice(0, 10);
}

const FlowPatternRecognitionPanel: React.FC<FlowPatternRecognitionPanelProps> = ({
  open,
  onClose,
  anchor,
  language,
  tapeSessionDayYmd,
  overlayZClass = 'z-[120]',
  compact = false,
}) => {
  useTheme();
  const [anchors, setAnchors] = useState<LegAnchor[]>([]);
  const [selected, setSelected] = useState<LegAnchor | null>(null);
  const [survey, setSurvey] = useState<LegSurvey | null>(null);
  const [sameExpiryOnly, setSameExpiryOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const session = useMemo(() => sessionOf(anchor, tapeSessionDayYmd), [anchor, tapeSessionDayYmd]);
  const isArabic = language === 'ar';

  // Step 1: which instants did this contract print at? Packages first.
  useEffect(() => {
    setAnchors([]); setSelected(null); setSurvey(null); setError(null);
    if (!open || !anchor) return;
    const ac = new AbortController();
    setLoading(true);
    fetchLegAnchors(
      {
        underlying: anchor.underlying,
        expiry: anchor.expiryYmd,
        strike: anchor.strike,
        right: anchor.right,
        sessionYmd: session,
      },
      ac.signal,
    )
      .then((list) => {
        setAnchors(list);
        // Auto-open the first FLAGGED instant. Landing on the largest print instead would
        // usually open a single-leg one, which surveys to nothing and reads as a failure.
        setSelected(list.find((a) => a.multiLeg) || list[0] || null);
      })
      .catch((e) => { if (!ac.signal.aborted) setError(String(e?.message || e)); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, [open, anchor, session]);

  // Step 2: everything that crossed at the chosen instant.
  useEffect(() => {
    setSurvey(null);
    if (!open || !anchor || !selected) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    fetchLegSurvey(
      {
        underlying: anchor.underlying,
        sessionYmd: session,
        msOfDay: selected.msOfDay,
        exchange: selected.exchange,
        expiry: sameExpiryOnly ? anchor.expiryYmd : undefined,
      },
      ac.signal,
    )
      .then((s) => setSurvey(s))
      .catch((e) => { if (!ac.signal.aborted) setError(String(e?.message || e)); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, [open, anchor, selected, sameExpiryOnly, session]);

  const classification = useMemo(() => {
    if (!survey?.legs.length) return null;
    const legs: NormalizedFlowLeg[] = survey.legs.map((l, i) => ({
      legId: `${l.expiry}|${l.strike}|${l.right}|${i}`,
      underlying: anchor?.underlying || '',
      expiryYmd: l.expiry,
      strike: l.strike,
      right: (l.right === 'P' ? 'P' : 'C') as NormalizedFlowLeg['right'],
      timestampMs: 0,
      volume: l.contracts,
      premium: l.premium,
      executionHint: (l.side === 'Ask' ? 'ask' : l.side === 'Bid' ? 'bid' : 'mid') as NormalizedFlowLeg['executionHint'],
    }));
    return classifyLinkedLegs(legs);
  }, [survey, anchor]);

  const onKey = useCallback((e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); }, [onClose]);
  useEffect(() => {
    if (!open) return;
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onKey]);

  if (!open || !anchor) return null;

  const bg = 'var(--elev)';
  const border = 'var(--border)';
  const muted = 'var(--muted)';
  const good = 'var(--up)';
  const warn = 'var(--warn)';
  const confirmed = survey?.package.confirmedByExchange;

  return (
    <div
      className={`fixed inset-0 ${overlayZClass} flex items-center justify-center p-4`}
      style={{ background: 'rgba(3,7,18,0.72)' }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="rounded-2xl border w-full max-w-5xl max-h-[88vh] overflow-auto"
        style={{ background: bg, borderColor: border, borderRadius: 12, boxShadow: 'var(--lift-3)', color: 'var(--txt)', fontFamily: 'var(--font-ui)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: border }}>
          <div>
            <h3 className="text-base font-semibold">
              {isArabic ? 'مسح الأرجل' : 'Leg survey'} — {anchor.underlying} {anchor.strike}
              {anchor.right} {anchor.expiryYmd}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: muted }}>
              {isArabic
                ? 'أرجل الصفقة الواحدة تُنفّذ في نفس اللحظة على نفس البورصة'
                : 'Legs of one package cross at the same instant on the same exchange — this reads the tape, it does not rank lookalikes.'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="mmec-btn">
            {isArabic ? 'إغلاق' : 'Close'}
          </button>
        </div>

        <div className="grid md:grid-cols-[280px_1fr] gap-0">
          {/* Instants — packages first */}
          <div className="border-r p-3" style={{ borderColor: border }}>
            <div className="mb-2" style={{ color: 'var(--muted-2)', fontSize: 10.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase' }}>
              {isArabic ? 'اللحظات' : 'PRINT INSTANTS'} ({anchors.length})
            </div>
            {anchors.length === 0 && !loading && (
              <div className="text-xs" style={{ color: muted }}>
                {isArabic ? 'لا توجد طبعات لهذا العقد في هذه الجلسة' : 'No prints for this contract in this session.'}
              </div>
            )}
            <div className="space-y-1">
              {anchors.map((a) => {
                const on = selected?.msOfDay === a.msOfDay && selected?.exchange === a.exchange;
                return (
                  <button
                    key={`${a.msOfDay}-${a.exchange}`}
                    type="button"
                    onClick={() => setSelected(a)}
                    className="w-full text-left px-2 py-1.5 rounded-lg border text-xs"
                    style={{
                      borderColor: on ? good : border,
                      background: on ? 'var(--up-dim)' : 'transparent',
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono">{msOfDayLabel(a.msOfDay)}</span>
                      {a.multiLeg && (
                        <span style={{ color: good, fontWeight: 700 }}>
                          {isArabic ? 'صفقة' : 'PACKAGE'}
                        </span>
                      )}
                    </div>
                    <div style={{ color: muted }}>
                      {a.contracts.toLocaleString()} × · {fmtUsd(a.premium)} · {a.prints} prints
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* The legs that crossed with it */}
          <div className="p-4">
            <label className="flex items-center gap-2 text-xs mb-3" style={{ color: muted }}>
              <input type="checkbox" checked={sameExpiryOnly} onChange={(e) => setSameExpiryOnly(e.target.checked)} />
              {isArabic
                ? 'نفس تاريخ الانتهاء فقط'
                : 'Same expiry only (off shows calendars and diagonals)'}
            </label>

            {loading && <div className="text-sm" style={{ color: muted }}>Loading…</div>}
            {error && (
              <div
                style={{
                  background: 'var(--down-dim)',
                  color: 'var(--down)',
                  border: '1px solid color-mix(in srgb, var(--down) 40%, transparent)',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 12.5,
                }}
              >
                {error}
              </div>
            )}

            {survey && (
              <>
                <div
                  className="rounded-xl border px-3 py-2 mb-3 text-sm"
                  style={{
                    borderColor: confirmed ? good : warn,
                    color: confirmed ? good : warn,
                    background: confirmed ? 'var(--up-dim)' : 'var(--warn-dim)',
                    fontSize: 12.5,
                  }}
                >
                  {confirmed ? (
                    <>
                      <strong>{isArabic ? 'صفقة مؤكدة من البورصة' : 'Package confirmed by the exchange'}</strong>
                      {' — '}{survey.package.conditionNames.join(', ')}
                      {survey.package.spansExpiries && (
                        <> · <strong>{isArabic ? 'تقويمي' : 'CALENDAR'}</strong> ({survey.package.expiries.join(' → ')})</>
                      )}
                    </>
                  ) : (
                    <>
                      <strong>{isArabic ? 'طبعات متزامنة' : 'Co-printed, not flagged'}</strong>
                      {' — '}
                      {isArabic
                        ? 'البورصة لم تُعلّم هذه الطبعة كصفقة مركّبة'
                        : 'the exchange did not mark these as one package, so they may simply have crossed together'}
                    </>
                  )}
                </div>

                {classification && !compact && (
                  <div className="text-xs mb-2" style={{ color: muted }}>
                    {isArabic ? 'الشكل' : 'Shape'}: <strong>{classification.labelKey.replace('flowPattern.', '')}</strong>
                    {' · '}{classification.confidence}
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full" style={{ fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                    <thead>
                      <tr style={{ color: muted, fontSize: 11, fontWeight: 600, letterSpacing: '.03em' }}>
                        <th className="text-left py-1 pr-3">{isArabic ? 'الانتهاء' : 'Expiry'}</th>
                        <th className="text-right py-1 pr-3">{isArabic ? 'التنفيذ' : 'Strike'}</th>
                        <th className="text-left py-1 pr-3">{isArabic ? 'النوع' : 'Right'}</th>
                        <th className="text-left py-1 pr-3">{isArabic ? 'الجهة' : 'Side'}</th>
                        <th className="text-right py-1 pr-3">{isArabic ? 'العقود' : 'Contracts'}</th>
                        <th className="text-right py-1 pr-3">{isArabic ? 'العلاوة' : 'Premium'}</th>
                        <th className="text-right py-1">{isArabic ? 'طبعات' : 'Prints'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {survey.legs.map((l, i) => {
                        const isAnchorLeg =
                          l.strike === anchor.strike && l.right === anchor.right && l.expiry === anchor.expiryYmd;
                        return (
                          <tr
                            key={`${l.expiry}-${l.strike}-${l.right}-${i}`}
                            style={{ fontWeight: isAnchorLeg ? 700 : 400, borderBottom: '1px solid var(--hair)' }}
                          >
                            <td className="py-1 pr-3 font-mono">{l.expiry}</td>
                            <td className="py-1 pr-3 text-right font-mono">{l.strike.toLocaleString()}</td>
                            <td className="py-1 pr-3">{l.right}</td>
                            <td className="py-1 pr-3">{l.side}</td>
                            <td className="py-1 pr-3 text-right font-mono">{l.contracts.toLocaleString()}</td>
                            <td className="py-1 pr-3 text-right font-mono">{fmtUsd(l.premium)}</td>
                            <td className="py-1 text-right font-mono" style={{ color: muted }}>{l.prints}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="text-xs mt-3" style={{ color: muted }}>
                  {survey.package.legCount} {isArabic ? 'رجل' : 'legs'} · {fmtUsd(survey.package.totalPremium)}{' '}
                  {isArabic ? 'إجمالي' : 'total'}
                </div>
              </>
            )}

            {survey && survey.legs.length <= 1 && (
              <div className="text-xs mt-2" style={{ color: muted }}>
                {isArabic
                  ? 'طبعة بساق واحدة — لا توجد أرجل أخرى في هذه اللحظة'
                  : 'Single-leg print — nothing else crossed at this instant. Try an instant marked PACKAGE.'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { FlowPatternRecognitionPanel };
export default FlowPatternRecognitionPanel;
